import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  EngineeringRoadmapBacklogPageDocument,
  EngineeringRoadmapDataDocument,
  EngineeringRoadmapDataQuery,
  EngineeringRoadmapBacklogPageQuery,
  EngineeringRoadmapMyBacklogPageDocument,
  EngineeringRoadmap__BacklogBucket,
  EngineeringRoadmap__BacklogListFilter,
  EngineeringRoadmap__BacklogWorkType,
  EngineeringRoadmap__Kind,
  EngineeringRoadmap__ReorderDirection,
  EngineeringRoadmap__Segment,
  EngineeringRoadmap__Status,
  Status,
  useEngineeringRoadmapBacklogItemCreateMutation,
  useEngineeringRoadmapBacklogItemDestroyMutation,
  useEngineeringRoadmapBacklogItemReorderMutation,
  useEngineeringRoadmapBacklogItemUpdateMutation,
  useEngineeringRoadmapBacklogPageQuery,
  useEngineeringRoadmapMyBacklogPageQuery,
  useEngineeringRoadmapDataQuery,
  useEngineeringRoadmapEntryCreateMutation,
  useEngineeringRoadmapEntryDestroyMutation,
  useEngineeringRoadmapEntryReorderMutation,
  useEngineeringRoadmapEntryUpdateMutation,
  useEngineeringRoadmapSettingUpsertMutation,
} from '@admin/schema';

/** Distinct from engineering segment slate — warm orange for OOO bars and legend. */
const OOO_BAR_BG = '#ea580c';

/** Full-fill colors for legend and timeline bars (distinct hues). */
const SEGMENT_COLOR: Record<EngineeringRoadmap__Segment, string> = {
  [EngineeringRoadmap__Segment.ServiceQuality]: '#0f766e',
  [EngineeringRoadmap__Segment.Capacity]: '#6d28d9',
  [EngineeringRoadmap__Segment.Data]: '#1d4ed8',
  [EngineeringRoadmap__Segment.Marketing]: '#be185d',
  [EngineeringRoadmap__Segment.Engineering]: '#475569',
};

const SEGMENT_LABELS: Record<EngineeringRoadmap__Segment, string> = {
  [EngineeringRoadmap__Segment.ServiceQuality]: 'Service Quality',
  [EngineeringRoadmap__Segment.Capacity]: 'Capacity',
  [EngineeringRoadmap__Segment.Data]: 'Data',
  [EngineeringRoadmap__Segment.Marketing]: 'Marketing',
  [EngineeringRoadmap__Segment.Engineering]: 'Engineering',
};

const SEGMENT_ORDER: EngineeringRoadmap__Segment[] = [
  EngineeringRoadmap__Segment.ServiceQuality,
  EngineeringRoadmap__Segment.Capacity,
  EngineeringRoadmap__Segment.Data,
  EngineeringRoadmap__Segment.Marketing,
  EngineeringRoadmap__Segment.Engineering,
];

/** Matches server `EngineeringRoadmapBacklogItem::MIN_BRIEF_CHARS_FOR_SPEC` for UI hints only. */
const SPEC_READY_MIN_BRIEF_CHARS = 80;

const WORK_TYPE_LABELS: Record<EngineeringRoadmap__BacklogWorkType, string> = {
  [EngineeringRoadmap__BacklogWorkType.Feature]: 'Feature',
  [EngineeringRoadmap__BacklogWorkType.Bug]: 'Bug',
  [EngineeringRoadmap__BacklogWorkType.Chore]: 'Chore',
  [EngineeringRoadmap__BacklogWorkType.Research]: 'Research / spike',
  [EngineeringRoadmap__BacklogWorkType.InfoRequest]: 'Information request',
};

const BACKLOG_WORK_TYPE_OPTIONS: EngineeringRoadmap__BacklogWorkType[] = [
  EngineeringRoadmap__BacklogWorkType.Feature,
  EngineeringRoadmap__BacklogWorkType.Bug,
  EngineeringRoadmap__BacklogWorkType.Chore,
  EngineeringRoadmap__BacklogWorkType.Research,
  EngineeringRoadmap__BacklogWorkType.InfoRequest,
];

const BRIEF_TEMPLATE_BY_WORK_TYPE: Record<EngineeringRoadmap__BacklogWorkType, string> = {
  [EngineeringRoadmap__BacklogWorkType.Feature]: `## Context (voice dictation OK)
Explain this like you’re asking a teammate for help: what’s going on, who cares, what triggered it?

## Current state
What exists today? What’s broken, missing, or confusing?

## Problem / outcome we want
What should change for users or the business?

## Goals
- 

## Success criteria (testable)
- 

## Things to avoid
- 

## Constraints
Timeline, tech, dependencies, teams, compliance, etc.

## Ideas & open questions (optional)
Half-formed thoughts, alternatives you’re considering.

## Links & attachments
- Figma / design:
- Screenshots / Loom / docs (URLs):
- Other references:
`,
  [EngineeringRoadmap__BacklogWorkType.Bug]: `## Context (voice dictation OK)
What were you doing when you noticed this? Who is affected?

## Current behavior vs expected

## Steps to reproduce
1. 

## Severity / customer impact

## Environment (app version, browser, warehouse, etc.)

## Things to avoid
Regressions or areas not to touch without checking.

## Links & attachments
Screenshots, screen recordings, Figma, logs (URLs):
`,
  [EngineeringRoadmap__BacklogWorkType.Chore]: `## Context (voice dictation OK)
Why does this need to happen?

## Current state

## What “done” looks like
- 

## Things to avoid

## Links & attachments
`,
  [EngineeringRoadmap__BacklogWorkType.Research]: `## Context (voice dictation OK)
What decision or unknown are we trying to resolve?

## Question(s) to answer

## Time box

## Outputs we need (doc, spike branch, recommendation)

## Stakeholders / who to loop in

## Links & attachments
`,
  [EngineeringRoadmap__BacklogWorkType.InfoRequest]: `## Context (voice dictation OK)
What decision or work is blocked?

## What we need to know

## Who might have the answer

## Urgency / deadline

## Links & attachments
`,
};

function buildCopyAiPromptForSpecReadyBacklog(p: {
  title: string;
  segment: EngineeringRoadmap__Segment;
  workType: EngineeringRoadmap__BacklogWorkType;
  briefBody: string;
  prdUrl: string;
  linearUrl: string;
}): string {
  const wt = WORK_TYPE_LABELS[p.workType];
  const seg = SEGMENT_LABELS[p.segment];
  const brief = p.briefBody.trim();
  const prd = p.prdUrl.trim();
  const lin = p.linearUrl.trim();
  const linkLines = [prd ? `- PRD / doc: ${prd}` : null, lin ? `- Linear: ${lin}` : null].filter(Boolean);
  return [
    'Please help plan and implement the following. Call out gaps or ambiguities before making large or risky changes.',
    '',
    `## Title\n${p.title}`,
    '',
    `## Team\n${seg}`,
    '',
    `## Type\n${wt}`,
    '',
    '## Spec',
    brief || '(No in-app brief text — see linked PRD/doc above if any.)',
    '',
    ...(linkLines.length ? ['## Links', ...linkLines, ''] : []),
  ].join('\n');
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  window.prompt('Copy to clipboard:', text);
}

function tabToBucketAndFilter(tab: TeamBacklogTab): {
  bucket: EngineeringRoadmap__BacklogBucket;
  filter: EngineeringRoadmap__BacklogListFilter;
} {
  if (tab === 'done') {
    return {
      bucket: EngineeringRoadmap__BacklogBucket.Completed,
      filter: EngineeringRoadmap__BacklogListFilter.All,
    };
  }
  const active = EngineeringRoadmap__BacklogBucket.Active;
  switch (tab) {
    case 'active_missing_prd':
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.MissingPrd };
    case 'active_on_roadmap':
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.OnRoadmap };
    case 'active_planned':
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.RoadmapPlanned };
    case 'active_in_progress':
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.RoadmapInProgress };
    case 'active_ready_for_qa':
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.RoadmapReadyForQa };
    case 'active_in_qa':
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.RoadmapInQa };
    default:
      return { bucket: active, filter: EngineeringRoadmap__BacklogListFilter.All };
  }
}

/** Priority arrows only reflect global order on the full active list; filtered tabs are browse-only for reorder. */
function tabAllowsReorder(tab: TeamBacklogTab): boolean {
  return tab === 'done' || tab === 'active_all';
}

function roadmapEntryStatusPhrase(status: EngineeringRoadmap__Status | null | undefined): string | null {
  if (status === EngineeringRoadmap__Status.InProgress) return 'In progress';
  if (status === EngineeringRoadmap__Status.ReadyForQa) return 'Ready for QA';
  if (status === EngineeringRoadmap__Status.InQa) return 'In QA';
  if (status === EngineeringRoadmap__Status.Planned) return 'Planned (not started)';
  if (status === EngineeringRoadmap__Status.Done) return 'Done';
  return null;
}

function hexAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const BACKLOG_PAGE_SIZE = 40;

type Entry = NonNullable<EngineeringRoadmapDataQuery['engineeringRoadmapEntries']>[number];
type RoadmapUser = NonNullable<EngineeringRoadmapDataQuery['engineeringRoadmapUsers']>[number];
type BacklogItem = EngineeringRoadmapBacklogPageQuery['engineeringRoadmapBacklogItemsPage']['rows'][number]['item'];

type TeamBacklogTab =
  | 'active_all'
  | 'active_missing_prd'
  | 'active_on_roadmap'
  | 'active_planned'
  | 'active_in_progress'
  | 'active_ready_for_qa'
  | 'active_in_qa'
  | 'done';
type TimelineItem = NonNullable<
  NonNullable<EngineeringRoadmapDataQuery['engineeringRoadmapTimeline']>[number]['queueItems'][number]
>;

function parseDate(d: string): Date {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatShortIsoDate(iso: string): string {
  const [y, m, day] = iso.split('-').map(Number);
  if (!y || !m || !day) return iso;
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Scheme-less strings (e.g. docs.google.com/...) resolve as paths on the current host in the browser.
 * Returns null if empty after trim.
 */
function absoluteExternalUrl(raw: string | null | undefined): string | null {
  const s = raw?.trim() ?? '';
  if (!s) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
  return `https://${s}`;
}

/** Extra buffer working days from % of core estimate, always rounded up (e.g. 1 day × 20% → +1 day). */
function bufferDaysRoundedUp(coreDays: number, bufferPercent: number): number {
  if (coreDays < 1) return 0;
  const bp = Math.max(0, Math.min(100, bufferPercent));
  return Math.ceil((coreDays * bp) / 100);
}

function scheduledTimelineWorkingDays(coreDays: number, bufferPercent: number): number {
  if (coreDays < 1) return 0;
  return coreDays + bufferDaysRoundedUp(coreDays, bufferPercent);
}

/** Timeline-derived copy for backlog rows (updates when roadmap data refetches). */
function backlogRoadmapScheduleText(
  item: BacklogItem,
  isDoneBacklogBucket: boolean,
  slotByEntryId: Map<string, { startDate: string; endDate: string }>,
): string | null {
  const re = item.roadmapEntry;
  if (!re) return null;
  const slot = slotByEntryId.get(re.id);
  const entryDone = re.status === EngineeringRoadmap__Status.Done;
  if (isDoneBacklogBucket || entryDone) {
    const d = re.actualEndDate ?? slot?.endDate;
    return d ? `Completed ${formatShortIsoDate(d)}` : 'Completed';
  }
  if (!slot) return 'Estimated completion date: …';
  return `Estimated completion date ${formatShortIsoDate(slot.endDate)}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Calendar-day difference in UTC to avoid DST edge cases. */
function diffDays(a: Date, b: Date): number {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / (1000 * 60 * 60 * 24));
}

/** First day of the cadence-aligned sprint period that contains forDate. */
function sprintPeriodStart(refStart: Date, cadenceDays: number, forDate: Date): Date {
  const ref = new Date(refStart.getFullYear(), refStart.getMonth(), refStart.getDate());
  const target = new Date(forDate.getFullYear(), forDate.getMonth(), forDate.getDate());
  const elapsed = diffDays(ref, target);
  const k = Math.floor(elapsed / cadenceDays);
  return addDays(ref, k * cadenceDays);
}

/**
 * First day of the default visible window: sprint containing today, or the reference
 * sprint start when today is still before that date (avoids showing a phantom prior period).
 */
function timelineViewStart(refStart: Date, cadenceDays: number, todayDate: Date): Date {
  const ref = new Date(refStart.getFullYear(), refStart.getMonth(), refStart.getDate());
  const t = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
  if (diffDays(ref, t) < 0) {
    return new Date(ref);
  }
  return sprintPeriodStart(refStart, cadenceDays, todayDate);
}

/** Alternating stripe index (0/1) for sprint banding on the timeline. */
function sprintStripeIndex(refStart: Date, cadenceDays: number, d: Date): number {
  const periodStart = sprintPeriodStart(refStart, cadenceDays, d);
  const idx = Math.floor(diffDays(refStart, periodStart) / cadenceDays);
  return Math.abs(idx) % 2;
}

function generateDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  let cur = new Date(start);
  while (cur <= end) {
    dates.push(new Date(cur));
    cur = addDays(cur, 1);
  }
  return dates;
}

const ROW_HEIGHT = 56;
const LABEL_WIDTH = 180;

const PageContainer = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  padding: 24px;
  background: #f8f9fb;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: #1a1f36;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  color: #374151;
  transition: all 0.15s;
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
`;

const PrimaryButton = styled.button`
  background: #4f86c6;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #3d6fa8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  background: #e8655a;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #d14a3e;
  }
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  padding: 6px 10px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 6px;
  transition: all 0.15s;
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const LegendSwatch = styled.div<{ c: string }>`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: ${(p) => p.c};
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.12);
`;

const SectionHeading = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px;
`;

const FormHint = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 6px 0 0;
  line-height: 1.4;
`;

/** Summary row on the planner — opens the full backlog sheet for that team. */
const TeamBacklogSummaryRow = styled.button<{ $segment: EngineeringRoadmap__Segment }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${(p) => SEGMENT_COLOR[p.$segment]};
  border-radius: 8px;
  background: ${(p) => hexAlpha(SEGMENT_COLOR[p.$segment], 0.1)};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  font-family: inherit;
  &:hover {
    background: ${(p) => hexAlpha(SEGMENT_COLOR[p.$segment], 0.16)};
  }
`;

const TeamBacklogBrowserSheet = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
`;

const TeamBacklogBrowserHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
`;

const TeamBacklogBrowserTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TeamBacklogBrowserTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const TeamBacklogBrowserMeta = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 400;
`;

const TeamBacklogBrowserHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TeamBacklogBrowserBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 22px 24px;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
`;

const TeamBacklogBrowserHint = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 16px;
  line-height: 1.45;
`;

const TeamBacklogTabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`;

const TeamBacklogTab = styled.button<{ $active: boolean }>`
  border: 1px solid ${(p) => (p.$active ? '#4f86c6' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#eef5fc' : '#fff')};
  color: ${(p) => (p.$active ? '#1e40af' : '#374151')};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: #93c5fd;
    background: ${(p) => (p.$active ? '#eef5fc' : '#f9fafb')};
  }
`;

const TeamBacklogPager = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
`;

const BacklogSection = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  overflow: hidden;
`;

const BacklogBody = styled.div`
  padding: 12px 18px 18px;
`;

const BacklogSubheading = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 12px 0 8px;
`;

const BacklogRow = styled.div<{ $highlight?: boolean; $segment: EngineeringRoadmap__Segment }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  column-gap: 20px;
  row-gap: 8px;
  align-items: start;
  padding: 12px 14px;
  min-height: 72px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  border-left: 3px solid ${(p) => SEGMENT_COLOR[p.$segment]};
  margin-bottom: 8px;
  font-size: 13px;
  background: ${(p) => hexAlpha(SEGMENT_COLOR[p.$segment], 0.07)};
  ${(p) =>
    p.$highlight
      ? css`
          box-shadow:
            0 0 0 2px ${SEGMENT_COLOR[p.$segment]},
            0 4px 14px ${hexAlpha(SEGMENT_COLOR[p.$segment], 0.25)};
        `
      : ''}
  &:hover {
    background: ${(p) => hexAlpha(SEGMENT_COLOR[p.$segment], 0.14)};
    border-color: #cbd5e1;
  }
`;

const BacklogRowTitle = styled.div`
  grid-column: 1;
  grid-row: 1;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: #111827;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BacklogRowDetails = styled.div`
  grid-column: 1;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  width: 100%;
`;

const backlogChipBase = css`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  padding: 3px 9px;
  border-radius: 6px;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 1px solid transparent;
`;

const BacklogRowBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const BacklogRowActions = styled.div`
  grid-column: 2;
  grid-row: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-content: center;
  gap: 6px;
  max-width: 340px;
  justify-self: end;
  align-self: center;
`;

const CriticalDateBadge = styled.span`
  ${backlogChipBase}
  color: #9a3412;
  background: #ffedd5;
  border-color: #fdba74;
`;

const RoadmapStatusBadge = styled.span<{ $onRoadmap: boolean }>`
  ${backlogChipBase}
  background: ${(p) => (p.$onRoadmap ? '#dcfce7' : '#f3f4f6')};
  color: ${(p) => (p.$onRoadmap ? '#166534' : '#64748b')};
  border-color: ${(p) => (p.$onRoadmap ? '#86efac' : '#e5e7eb')};
`;

const MissingSpecBadge = styled.span`
  ${backlogChipBase}
  color: #b45309;
  background: #fffbeb;
  border-color: #fcd34d;
`;

const SpecReadyBadge = styled.span`
  ${backlogChipBase}
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
`;

const WorkTypeChip = styled.span`
  ${backlogChipBase}
  color: #475569;
  background: #f8fafc;
  border-color: #e2e8f0;
`;

const SegmentTeamBadge = styled.span<{ $segment: EngineeringRoadmap__Segment }>`
  ${backlogChipBase}
  color: #334155;
  background: #f1f5f9;
  border-color: #cbd5e1;
  border-left: 3px solid ${(p) => SEGMENT_COLOR[p.$segment]};
`;

/** Same chip shell as other badges; opens PRD in a new tab. */
const PrdOpenBadge = styled.a`
  ${backlogChipBase}
  color: #1e40af;
  background: #eff6ff;
  border-color: #93c5fd;
  text-decoration: none;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: #dbeafe;
    border-color: #3b82f6;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }
`;

const LinearOpenBadge = styled.a`
  ${backlogChipBase}
  color: #3730a3;
  background: #eef2ff;
  border-color: #c7d2fe;
  text-decoration: none;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: #e0e7ff;
    border-color: #6366f1;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  &:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: 1px;
  }
`;

const RoadmapScheduleBadge = styled.span`
  ${backlogChipBase}
  color: #5b21b6;
  background: #f5f3ff;
  border-color: #ddd6fe;
`;

const OwnerMeta = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
  width: 100%;
`;

const SmallButton = styled.button`
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  color: #374151;
  font-family: inherit;
  &:hover {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const TimelineWrapper = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.06);
  overflow-x: auto;
  position: relative;
  width: 100%;
  min-width: 0;
`;

const TimelineChrome = styled.div`
  width: 100%;
  min-width: 0;
`;

/** Aligns engineer label column with each row’s bar area (same as RowContainer). */
const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${LABEL_WIDTH}px minmax(0, 1fr);
  width: 100%;
  min-width: 0;
`;

const EngineerCorner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-right: 1px solid #e5e7eb;
`;

const TimelineHeaderBlock = styled.div`
  min-width: 0;
  width: 100%;
`;

const MonthHeaderCell = styled.div<{ $flex: number }>`
  flex: ${(p) => p.$flex} ${(p) => p.$flex} 0;
  min-width: 0;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 0 2px;
  border-right: 1px solid #f3f4f6;
`;

const DayCell = styled.div<{ isWeekend: boolean; isToday: boolean; sprintStripe: number }>`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  font-size: 10px;
  padding: 4px 0;
  background: ${(p) => {
    if (p.isToday) return '#EFF6FF';
    if (p.sprintStripe === 1) return p.isWeekend ? '#f3f4f6' : '#f0f9ff';
    return p.isWeekend ? '#f9fafb' : '#fafafa';
  }};
  color: ${(p) => (p.isToday ? '#2563eb' : p.isWeekend ? '#9ca3af' : '#6b7280')};
  font-weight: ${(p) => (p.isToday ? 600 : 400)};
  border-right: 1px solid #f3f4f6;
  line-height: 1.3;
  box-shadow: ${(p) => (p.sprintStripe === 0 ? 'inset 1px 0 0 #e5e7eb' : 'none')};
`;

const RowContainer = styled.div<{ isEven: boolean }>`
  display: grid;
  grid-template-columns: ${LABEL_WIDTH}px minmax(0, 1fr);
  width: 100%;
  min-width: 0;
  min-height: ${ROW_HEIGHT}px;
  background: ${(p) => (p.isEven ? '#fafbfc' : '#fff')};
  border-bottom: 1px solid #f3f4f6;
  &:hover {
    background: ${(p) => (p.isEven ? '#eef2ff' : '#f0f4ff')};
  }
`;

const UserLabel = styled.button`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #1a1f36;
  display: flex;
  align-items: center;
  gap: 8px;
  border-right: 1px solid #e5e7eb;
  position: sticky;
  left: 0;
  background: inherit;
  z-index: 2;
  cursor: pointer;
  border: none;
  text-align: left;
  width: 100%;
  font-family: inherit;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

const BarArea = styled.div`
  position: relative;
  min-height: ${ROW_HEIGHT}px;
  width: 100%;
  min-width: 0;
`;

const EntryBar = styled.button<{ leftPct: number; widthPct: number; bg: string }>`
  position: absolute;
  left: ${(p) => p.leftPct}%;
  width: ${(p) => Math.max(p.widthPct, 0.2)}%;
  min-width: 6px;
  box-sizing: border-box;
  top: 10px;
  height: 36px;
  background: ${(p) => p.bg};
  border-left: 3px solid rgba(15, 23, 42, 0.2);
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition:
    filter 0.15s,
    transform 0.1s;
  z-index: 1;
  border-top: none;
  border-right: none;
  border-bottom: none;
  font-family: inherit;
  &:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }
`;

const TodayLine = styled.div<{ leftPct: number }>`
  position: absolute;
  left: ${(p) => p.leftPct}%;
  transform: translateX(-50%);
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ef4444;
  z-index: 3;
  pointer-events: none;
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -3px;
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
  }
`;

const Tooltip = styled.div`
  position: fixed;
  background: #1f2937;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  max-width: 320px;
  z-index: 1001;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  line-height: 1.4;
  white-space: pre-wrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

/** Same backdrop as other modals, with padding for a large centered sheet (defined after ModalOverlay for styled order). */
const TeamBacklogBrowserOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  width: 440px;
  max-width: 95vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
`;

/**
 * Flex column: pinned header + scrollable body + pinned footer.
 * Shared by roadmap entry, backlog, and settings modals so actions stay visible when content grows.
 */
const ScrollModalContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
  padding: 0;
  overflow: hidden;
`;

const ScrollModalHeader = styled.div`
  flex-shrink: 0;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #f3f4f6;
`;

const ScrollModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 22px 12px;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
`;

const ScrollModalFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 22px 18px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 -10px 24px -16px rgba(15, 23, 42, 0.12);
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px;
  color: #1a1f36;
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.15s;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #4f86c6;
    box-shadow: 0 0 0 3px rgba(79, 134, 198, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #4f86c6;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 60px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #4f86c6;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const ScrollModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #9ca3af;
  font-size: 14px;
`;

const DateRangeDisplay = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  color: #991b1b;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
`;

interface EntryFormData {
  userID: string;
  title: string;
  kind: string;
  segment: EngineeringRoadmap__Segment;
  productOwnerId: string;
  engineeringOwnerId: string;
  startDate: string;
  endDate: string;
  actualStartDate: string;
  actualEndDate: string;
  estimatedDurationDays: number | '';
  bufferPercent: number;
  status: string;
  color: string;
  note: string;
}

interface BacklogFormData {
  segment: EngineeringRoadmap__Segment;
  title: string;
  workType: EngineeringRoadmap__BacklogWorkType;
  briefBody: string;
  prdUrl: string;
  linearUrl: string;
  targetReleaseDate: string;
  productOwnerId: string;
  engineeringOwnerId: string;
}

const refetchVars = (anchorDate: string) => ({
  query: EngineeringRoadmapDataDocument,
  variables: { anchorDate },
});

type BacklogSheetRefetchVars = {
  segment: EngineeringRoadmap__Segment;
  bucket: EngineeringRoadmap__BacklogBucket;
  filter: EngineeringRoadmap__BacklogListFilter;
  page: number;
};

type MyWorkSheetRefetchVars = {
  bucket: EngineeringRoadmap__BacklogBucket;
  filter: EngineeringRoadmap__BacklogListFilter;
  page: number;
};

function refetchRoadmapAndBacklogSheets(
  anchorDate: string,
  teamSheet: BacklogSheetRefetchVars | null,
  myWorkSheet: MyWorkSheetRefetchVars | null,
) {
  return {
    refetchQueries: [
      refetchVars(anchorDate),
      ...(teamSheet
        ? [
            {
              query: EngineeringRoadmapBacklogPageDocument,
              variables: {
                segment: teamSheet.segment,
                bucket: teamSheet.bucket,
                filter: teamSheet.filter,
                page: teamSheet.page,
                perPage: BACKLOG_PAGE_SIZE,
              },
            },
          ]
        : []),
      ...(myWorkSheet
        ? [
            {
              query: EngineeringRoadmapMyBacklogPageDocument,
              variables: {
                bucket: myWorkSheet.bucket,
                filter: myWorkSheet.filter,
                page: myWorkSheet.page,
                perPage: BACKLOG_PAGE_SIZE,
              },
            },
          ]
        : []),
    ],
    awaitRefetchQueries: true,
  };
}

export const EngineeringRoadmapPlanner: React.FC = () => {
  useTitle('Engineering Capacity Planner');
  const today = useMemo(() => new Date(), []);
  const todayStr = formatDate(today);

  const { data, loading, error } = useEngineeringRoadmapDataQuery({
    client,
    variables: { anchorDate: todayStr },
    fetchPolicy: 'network-only',
  });

  const settings = data?.engineeringRoadmapSetting;
  const refStartStr = settings?.referenceStartDate ?? todayStr;
  const refStart = useMemo(() => parseDate(refStartStr), [refStartStr]);
  const cadenceDays = settings?.cadenceDays ?? 14;

  const viewRangeStart = useMemo(() => timelineViewStart(refStart, cadenceDays, today), [refStart, cadenceDays, today]);
  const defaultRangeEnd = useMemo(() => addDays(viewRangeStart, cadenceDays - 1), [viewRangeStart, cadenceDays]);

  const [rangeStart, setRangeStart] = useState<Date>(() => viewRangeStart);
  const [rangeEnd, setRangeEnd] = useState<Date>(() => defaultRangeEnd);
  const [rangeSyncedToSprint, setRangeSyncedToSprint] = useState(true);

  useEffect(() => {
    if (!rangeSyncedToSprint) return;
    setRangeStart(viewRangeStart);
    setRangeEnd(defaultRangeEnd);
  }, [viewRangeStart, defaultRangeEnd, rangeSyncedToSprint]);

  const visibleDayCount = useMemo(() => diffDays(rangeStart, rangeEnd) + 1, [rangeStart, rangeEnd]);

  const [createEntry] = useEngineeringRoadmapEntryCreateMutation({ client });
  const [updateEntry] = useEngineeringRoadmapEntryUpdateMutation({ client });
  const [reorderEntry] = useEngineeringRoadmapEntryReorderMutation({ client });
  const [destroyEntry] = useEngineeringRoadmapEntryDestroyMutation({ client });
  const [upsertSetting] = useEngineeringRoadmapSettingUpsertMutation({ client });
  const [createBacklogItem] = useEngineeringRoadmapBacklogItemCreateMutation({ client });
  const [updateBacklogItem] = useEngineeringRoadmapBacklogItemUpdateMutation({ client });
  const [destroyBacklogItem] = useEngineeringRoadmapBacklogItemDestroyMutation({ client });
  const [reorderBacklogItem] = useEngineeringRoadmapBacklogItemReorderMutation({ client });

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    referenceStartDate: '',
    cadenceDays: 14,
    defaultBufferPercent: 20,
  });
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Entry | TimelineItem | null>(null);
  const [editingQueueContext, setEditingQueueContext] = useState<{
    queueIndex: number;
    queueLength: number;
  } | null>(null);
  const [pendingBacklogItemId, setPendingBacklogItemId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EntryFormData>({
    userID: '',
    title: '',
    kind: EngineeringRoadmap__Kind.Project,
    segment: EngineeringRoadmap__Segment.Engineering,
    productOwnerId: '',
    engineeringOwnerId: '',
    startDate: '',
    endDate: '',
    actualStartDate: '',
    actualEndDate: '',
    estimatedDurationDays: 7,
    bufferPercent: 20,
    status: EngineeringRoadmap__Status.Planned,
    color: '',
    note: '',
  });
  const [backlogModalOpen, setBacklogModalOpen] = useState(false);
  const [editingBacklogItem, setEditingBacklogItem] = useState<BacklogItem | null>(null);
  const [backlogForm, setBacklogForm] = useState<BacklogFormData>({
    segment: EngineeringRoadmap__Segment.Engineering,
    title: '',
    workType: EngineeringRoadmap__BacklogWorkType.Feature,
    briefBody: '',
    prdUrl: '',
    linearUrl: '',
    targetReleaseDate: '',
    productOwnerId: '',
    engineeringOwnerId: '',
  });
  /** Full-screen-style sheet for one team’s backlog (server-paged lists per tab). */
  const [teamBacklogBrowserSegment, setTeamBacklogBrowserSegment] = useState<EngineeringRoadmap__Segment | null>(null);
  const [teamBacklogTab, setTeamBacklogTab] = useState<TeamBacklogTab>('active_all');
  const [teamBacklogPage, setTeamBacklogPage] = useState(1);
  /** Cross-team view: rows where you are PO, EO, or the linked roadmap assignee. */
  const [myWorkBrowserOpen, setMyWorkBrowserOpen] = useState(false);
  const [myWorkTab, setMyWorkTab] = useState<TeamBacklogTab>('active_all');
  const [myWorkPage, setMyWorkPage] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [backlogFormError, setBacklogFormError] = useState<string | null>(null);
  /** After create: open team sheet and scroll to the new row (lowest priority = end of list). */
  const [backlogPostCreateFocus, setBacklogPostCreateFocus] = useState<{
    id: string;
    segment: EngineeringRoadmap__Segment;
  } | null>(null);
  const [backlogHighlightId, setBacklogHighlightId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const users = data?.engineeringRoadmapUsers ?? [];
  const productOwners = data?.engineeringRoadmapAssignableProductOwners ?? [];
  const engineeringOwners = data?.engineeringRoadmapAssignableEngineeringOwners ?? [];
  const entries = data?.engineeringRoadmapEntries ?? [];
  const backlogSummaries = data?.engineeringRoadmapBacklogSegmentSummaries ?? [];
  const myWorkSummary = data?.engineeringRoadmapMyBacklogSummary;
  const timeline = data?.engineeringRoadmapTimeline ?? [];
  const timelineSlotByEntryId = useMemo(() => {
    const m = new Map<string, { startDate: string; endDate: string }>();
    for (const row of timeline) {
      for (const qi of row.queueItems) {
        m.set(qi.entry.id, { startDate: qi.startDate, endDate: qi.endDate });
      }
    }
    return m;
  }, [timeline]);

  const dates = useMemo(() => generateDateRange(rangeStart, rangeEnd), [rangeStart, rangeEnd]);

  const summaryBySegment = useMemo(() => {
    const map = new Map<
      EngineeringRoadmap__Segment,
      { activeCount: number; completedCount: number; activeMissingSpecCount: number }
    >();
    for (const s of SEGMENT_ORDER) {
      map.set(s, { activeCount: 0, completedCount: 0, activeMissingSpecCount: 0 });
    }
    for (const row of backlogSummaries) {
      map.set(row.segment, {
        activeCount: row.activeCount,
        completedCount: row.completedCount,
        activeMissingSpecCount: row.activeMissingSpecCount,
      });
    }
    return map;
  }, [backlogSummaries]);

  const { bucket: sheetBucket, filter: sheetFilter } = tabToBucketAndFilter(teamBacklogTab);
  const { bucket: myWorkBucket, filter: myWorkFilter } = tabToBucketAndFilter(myWorkTab);
  const {
    data: backlogPageData,
    loading: backlogPageLoading,
    error: backlogPageError,
  } = useEngineeringRoadmapBacklogPageQuery({
    client,
    skip: teamBacklogBrowserSegment === null,
    variables: {
      segment: teamBacklogBrowserSegment ?? EngineeringRoadmap__Segment.Engineering,
      bucket: sheetBucket,
      filter: sheetFilter,
      page: teamBacklogPage,
      perPage: BACKLOG_PAGE_SIZE,
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: myBacklogPageData,
    loading: myBacklogPageLoading,
    error: myBacklogPageError,
  } = useEngineeringRoadmapMyBacklogPageQuery({
    client,
    skip: !myWorkBrowserOpen,
    variables: {
      bucket: myWorkBucket,
      filter: myWorkFilter,
      page: myWorkPage,
      perPage: BACKLOG_PAGE_SIZE,
    },
    fetchPolicy: 'network-only',
  });

  const backlogSheetRef = useRef<BacklogSheetRefetchVars | null>(null);
  if (teamBacklogBrowserSegment === null) {
    backlogSheetRef.current = null;
  } else {
    backlogSheetRef.current = {
      segment: teamBacklogBrowserSegment,
      bucket: sheetBucket,
      filter: sheetFilter,
      page: teamBacklogPage,
    };
  }

  const myWorkSheetRef = useRef<MyWorkSheetRefetchVars | null>(null);
  if (!myWorkBrowserOpen) {
    myWorkSheetRef.current = null;
  } else {
    myWorkSheetRef.current = {
      bucket: myWorkBucket,
      filter: myWorkFilter,
      page: myWorkPage,
    };
  }

  useEffect(() => {
    const resolved = backlogPageData?.engineeringRoadmapBacklogItemsPage?.page;
    if (resolved == null || teamBacklogBrowserSegment === null) return;
    if (resolved !== teamBacklogPage) setTeamBacklogPage(resolved);
  }, [backlogPageData?.engineeringRoadmapBacklogItemsPage?.page, teamBacklogPage, teamBacklogBrowserSegment]);

  useEffect(() => {
    const resolved = myBacklogPageData?.engineeringRoadmapMyBacklogItemsPage?.page;
    if (resolved == null || !myWorkBrowserOpen) return;
    if (resolved !== myWorkPage) setMyWorkPage(resolved);
  }, [myBacklogPageData?.engineeringRoadmapMyBacklogItemsPage?.page, myWorkPage, myWorkBrowserOpen]);

  useEffect(() => {
    if (!backlogPostCreateFocus) return;
    const { id, segment } = backlogPostCreateFocus;
    const summary = summaryBySegment.get(segment);
    if (!summary) return;

    const lastPage = Math.max(1, Math.ceil(summary.activeCount / BACKLOG_PAGE_SIZE));
    setTeamBacklogBrowserSegment(segment);
    setTeamBacklogTab('active_all');
    setTeamBacklogPage(lastPage);
    setBacklogHighlightId(id);

    let rafOuter = 0;
    let rafInner = 0;
    rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(() => {
        const safeId = typeof CSS !== 'undefined' && 'escape' in CSS ? CSS.escape(id) : id.replace(/"/g, '');
        document.querySelector(`[data-backlog-item-id="${safeId}"]`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    });

    const clearTimer = window.setTimeout(() => {
      setBacklogPostCreateFocus(null);
      setBacklogHighlightId(null);
    }, 2400);

    return () => {
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
      window.clearTimeout(clearTimer);
    };
  }, [backlogPostCreateFocus, summaryBySegment]);

  const oooByUser = useMemo(() => {
    const map = new Map<string, Entry[]>();
    for (const e of entries) {
      if (e.kind === EngineeringRoadmap__Kind.OutOfOffice && e.startDate && e.endDate) {
        const list = map.get(e.user.id) ?? [];
        list.push(e);
        map.set(e.user.id, list);
      }
    }
    return map;
  }, [entries]);

  const months = useMemo(() => {
    const result: { label: string; span: number }[] = [];
    let current = '';
    let count = 0;
    for (const d of dates) {
      const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (label !== current) {
        if (current) result.push({ label: current, span: count });
        current = label;
        count = 1;
      } else {
        count++;
      }
    }
    if (current) result.push({ label: current, span: count });
    return result;
  }, [dates]);

  const todayLeftPct = useMemo(() => {
    const idx = dates.findIndex((d) => formatDate(d) === todayStr);
    if (idx < 0 || dates.length === 0) return -1;
    return ((idx + 0.5) / dates.length) * 100;
  }, [dates, todayStr]);

  const navigate = useCallback(
    (direction: 'back' | 'forward') => {
      const shift = direction === 'back' ? -visibleDayCount : visibleDayCount;
      setRangeSyncedToSprint(false);
      setRangeStart((s) => addDays(s, shift));
      setRangeEnd((e) => addDays(e, shift));
    },
    [visibleDayCount],
  );

  const snapToCurrentSprint = useCallback(() => {
    setRangeSyncedToSprint(true);
  }, []);

  const openSettingsModal = useCallback(() => {
    setSettingsError(null);
    if (settings) {
      setSettingsForm({
        referenceStartDate: settings.referenceStartDate,
        cadenceDays: settings.cadenceDays,
        defaultBufferPercent: settings.defaultBufferPercent,
      });
    } else {
      setSettingsForm({
        referenceStartDate: todayStr,
        cadenceDays: 14,
        defaultBufferPercent: 20,
      });
    }
    setSettingsModalOpen(true);
  }, [settings, todayStr]);

  const saveSettings = useCallback(async () => {
    setSubmitting(true);
    setSettingsError(null);
    try {
      const { data: result } = await upsertSetting({
        variables: settingsForm,
        ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
      });
      if (result?.engineeringRoadmapSettingUpsert?.status !== 'OK') {
        setSettingsError(result?.engineeringRoadmapSettingUpsert?.error ?? 'Failed to save settings');
        return;
      }
      setRangeSyncedToSprint(true);
      setSettingsModalOpen(false);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  }, [settingsForm, upsertSetting, todayStr]);

  const openCreateModal = useCallback(
    (userId?: string, isOoo = true, fromBacklog?: BacklogItem | null) => {
      if (users.length === 0) return;
      const eoId = fromBacklog?.engineeringOwner?.id;
      const defaultFromEo = !userId && eoId && users.some((u) => u.id === eoId) ? eoId : undefined;
      const userID = userId || defaultFromEo || users[0]?.id || '';
      if (!userID) return;

      setEditingEntry(null);
      setPendingBacklogItemId(fromBacklog?.id ?? null);
      setFormData({
        userID,
        title: fromBacklog?.title ?? '',
        kind: isOoo ? EngineeringRoadmap__Kind.OutOfOffice : EngineeringRoadmap__Kind.Project,
        segment: fromBacklog?.segment ?? EngineeringRoadmap__Segment.Engineering,
        productOwnerId: fromBacklog?.productOwner?.id ?? '',
        engineeringOwnerId: fromBacklog?.engineeringOwner?.id ?? '',
        startDate: isOoo ? todayStr : '',
        endDate: isOoo ? formatDate(addDays(today, 6)) : '',
        actualStartDate: '',
        actualEndDate: '',
        estimatedDurationDays: 7,
        bufferPercent: settings?.defaultBufferPercent ?? 20,
        status: EngineeringRoadmap__Status.Planned,
        color: '',
        note: '',
      });
      setFormError(null);
      setEntryModalOpen(true);
    },
    [users, todayStr, today, settings],
  );

  const openEditModal = useCallback(
    (item: Entry | TimelineItem, queueContext?: { queueIndex: number; queueLength: number }) => {
      const entry = 'entry' in item ? item.entry : item;
      const isOoo = entry.kind === EngineeringRoadmap__Kind.OutOfOffice;
      const startDate = ('startDate' in item ? item.startDate : (item as Entry).startDate) ?? '';
      const endDate = ('endDate' in item ? item.endDate : (item as Entry).endDate) ?? '';
      setEditingEntry(item);
      setPendingBacklogItemId(null);
      setEditingQueueContext(isOoo ? null : (queueContext ?? null));
      setFormData({
        userID: entry.user.id,
        title: entry.title,
        kind: entry.kind,
        segment: entry.segment ?? EngineeringRoadmap__Segment.Engineering,
        productOwnerId: entry.productOwner?.id ?? '',
        engineeringOwnerId: entry.engineeringOwner?.id ?? '',
        startDate,
        endDate,
        actualStartDate: entry.actualStartDate ?? '',
        actualEndDate: entry.actualEndDate ?? '',
        estimatedDurationDays: entry.estimatedDurationDays ?? 7,
        bufferPercent: entry.bufferPercent ?? 20,
        status: (entry.status ?? EngineeringRoadmap__Status.Planned) as EngineeringRoadmap__Status,
        color: entry.color ?? '',
        note: entry.note ?? '',
      });
      setFormError(null);
      setEntryModalOpen(true);
    },
    [],
  );

  const closeEntryModal = useCallback(() => {
    setEntryModalOpen(false);
    setEditingEntry(null);
    setEditingQueueContext(null);
    setPendingBacklogItemId(null);
    setFormError(null);
  }, []);

  const openBacklogModal = useCallback((segment: EngineeringRoadmap__Segment, existing: BacklogItem | null) => {
    setBacklogFormError(null);
    setEditingBacklogItem(existing);
    if (existing) {
      setBacklogForm({
        segment: existing.segment,
        title: existing.title,
        workType: existing.workType,
        briefBody: existing.briefBody ?? '',
        prdUrl: existing.prdURL ?? '',
        linearUrl: existing.linearURL ?? '',
        targetReleaseDate: existing.targetReleaseDate ?? '',
        productOwnerId: existing.productOwner?.id ?? '',
        engineeringOwnerId: existing.engineeringOwner?.id ?? '',
      });
    } else {
      setBacklogForm({
        segment,
        title: '',
        workType: EngineeringRoadmap__BacklogWorkType.Feature,
        briefBody: '',
        prdUrl: '',
        linearUrl: '',
        targetReleaseDate: '',
        productOwnerId: '',
        engineeringOwnerId: '',
      });
    }
    setBacklogModalOpen(true);
  }, []);

  const closeBacklogModal = useCallback(() => {
    setBacklogModalOpen(false);
    setEditingBacklogItem(null);
    setBacklogFormError(null);
  }, []);

  const closeTeamBacklogBrowser = useCallback(() => {
    setTeamBacklogBrowserSegment(null);
    setTeamBacklogTab('active_all');
    setTeamBacklogPage(1);
  }, []);

  const closeMyWorkBrowser = useCallback(() => {
    setMyWorkBrowserOpen(false);
    setMyWorkTab('active_all');
    setMyWorkPage(1);
  }, []);

  const openTeamBacklogBrowser = useCallback((seg: EngineeringRoadmap__Segment) => {
    setMyWorkBrowserOpen(false);
    setTeamBacklogBrowserSegment(seg);
    setTeamBacklogTab('active_all');
    setTeamBacklogPage(1);
  }, []);

  const openMyWorkBrowser = useCallback(() => {
    setTeamBacklogBrowserSegment(null);
    setMyWorkBrowserOpen(true);
    setMyWorkTab('active_all');
    setMyWorkPage(1);
  }, []);

  const selectTeamBacklogTab = useCallback((t: TeamBacklogTab) => {
    setTeamBacklogTab(t);
    setTeamBacklogPage(1);
  }, []);

  const selectMyWorkTab = useCallback((t: TeamBacklogTab) => {
    setMyWorkTab(t);
    setMyWorkPage(1);
  }, []);

  useEffect(() => {
    if (
      !settingsModalOpen &&
      !entryModalOpen &&
      !backlogModalOpen &&
      teamBacklogBrowserSegment === null &&
      !myWorkBrowserOpen
    )
      return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (settingsModalOpen) setSettingsModalOpen(false);
        else if (backlogModalOpen) closeBacklogModal();
        else if (myWorkBrowserOpen) closeMyWorkBrowser();
        else if (teamBacklogBrowserSegment !== null) closeTeamBacklogBrowser();
        else closeEntryModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    settingsModalOpen,
    entryModalOpen,
    backlogModalOpen,
    teamBacklogBrowserSegment,
    myWorkBrowserOpen,
    closeEntryModal,
    closeBacklogModal,
    closeTeamBacklogBrowser,
    closeMyWorkBrowser,
  ]);

  const handleEntrySubmit = useCallback(async () => {
    const isOoo = formData.kind === EngineeringRoadmap__Kind.OutOfOffice;
    const effectiveTitle = isOoo && !formData.title.trim() ? 'Out of Office' : formData.title.trim();
    if (!effectiveTitle) {
      setFormError('Title is required');
      return;
    }

    if (isOoo && (!formData.startDate || !formData.endDate)) {
      setFormError('Start and end dates are required for Out of Office');
      return;
    }
    if (isOoo && formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      setFormError('Start date must be on or before end date');
      return;
    }
    if (!isOoo && (!formData.estimatedDurationDays || formData.estimatedDurationDays < 1)) {
      setFormError('Duration is required (at least 1 working day)');
      return;
    }
    if (!isOoo && (formData.bufferPercent < 0 || formData.bufferPercent > 100)) {
      setFormError('Buffer must be between 0 and 100 percent');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    const refetch = refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current);

    try {
      if (editingEntry) {
        const entry = 'entry' in editingEntry ? editingEntry.entry : editingEntry;
        const { data: result } = await updateEntry({
          variables: {
            id: entry.id,
            title: effectiveTitle,
            kind: formData.kind as EngineeringRoadmap__Kind,
            segment: isOoo ? undefined : formData.segment,
            productOwnerId: isOoo ? undefined : formData.productOwnerId || null,
            engineeringOwnerId: isOoo ? undefined : formData.engineeringOwnerId || null,
            startDate: isOoo ? formData.startDate || null : null,
            endDate: isOoo ? formData.endDate || null : null,
            actualStartDate: isOoo ? null : formData.actualStartDate || null,
            actualEndDate: isOoo ? null : formData.actualEndDate || null,
            estimatedDurationDays: isOoo
              ? null
              : typeof formData.estimatedDurationDays === 'number'
                ? formData.estimatedDurationDays
                : undefined,
            bufferPercent: isOoo ? undefined : formData.bufferPercent,
            status: isOoo ? null : (formData.status as EngineeringRoadmap__Status),
            note: formData.note || null,
            color: formData.color || null,
          },
          ...refetch,
        });
        if (result?.engineeringRoadmapEntryUpdate?.status !== 'OK') {
          setFormError(result?.engineeringRoadmapEntryUpdate?.error ?? 'Failed to update');
          setSubmitting(false);
          return;
        }
      } else {
        const { data: result } = await createEntry({
          variables: {
            userID: formData.userID,
            title: effectiveTitle,
            kind: formData.kind as EngineeringRoadmap__Kind,
            segment: isOoo ? undefined : formData.segment,
            productOwnerId: isOoo ? undefined : formData.productOwnerId || null,
            engineeringOwnerId: isOoo ? undefined : formData.engineeringOwnerId || null,
            backlogItemId: pendingBacklogItemId || null,
            startDate: isOoo ? formData.startDate || null : null,
            endDate: isOoo ? formData.endDate || null : null,
            estimatedDurationDays: isOoo
              ? null
              : typeof formData.estimatedDurationDays === 'number'
                ? formData.estimatedDurationDays
                : undefined,
            bufferPercent: isOoo ? undefined : formData.bufferPercent,
            status: isOoo ? null : (formData.status as EngineeringRoadmap__Status),
            note: formData.note || null,
            color: formData.color || null,
          },
          ...refetch,
        });
        if (result?.engineeringRoadmapEntryCreate?.status !== 'OK') {
          setFormError(result?.engineeringRoadmapEntryCreate?.error ?? 'Failed to create');
          setSubmitting(false);
          return;
        }
      }
      closeEntryModal();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  }, [formData, editingEntry, createEntry, updateEntry, todayStr, closeEntryModal, pendingBacklogItemId]);

  const handleBacklogSubmit = useCallback(async () => {
    if (!backlogForm.title.trim()) {
      setBacklogFormError('Title is required');
      return;
    }
    if (!backlogForm.productOwnerId.trim()) {
      setBacklogFormError('Product owner is required');
      return;
    }
    setSubmitting(true);
    setBacklogFormError(null);
    const refetch = refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current);
    try {
      if (editingBacklogItem) {
        const { data: result } = await updateBacklogItem({
          variables: {
            id: editingBacklogItem.id,
            title: backlogForm.title.trim(),
            workType: backlogForm.workType,
            briefBody: backlogForm.briefBody.trim() || null,
            prdUrl: absoluteExternalUrl(backlogForm.prdUrl),
            linearUrl: absoluteExternalUrl(backlogForm.linearUrl),
            targetReleaseDate: backlogForm.targetReleaseDate || null,
            productOwnerId: backlogForm.productOwnerId,
            engineeringOwnerId: backlogForm.engineeringOwnerId || null,
            segment: backlogForm.segment,
          },
          ...refetch,
        });
        if (result?.engineeringRoadmapBacklogItemUpdate?.status !== 'OK') {
          setBacklogFormError(result?.engineeringRoadmapBacklogItemUpdate?.error ?? 'Update failed');
          setSubmitting(false);
          return;
        }
      } else {
        const { data: result } = await createBacklogItem({
          variables: {
            segment: backlogForm.segment,
            title: backlogForm.title.trim(),
            workType: backlogForm.workType,
            briefBody: backlogForm.briefBody.trim() || null,
            prdUrl: absoluteExternalUrl(backlogForm.prdUrl),
            linearUrl: absoluteExternalUrl(backlogForm.linearUrl),
            targetReleaseDate: backlogForm.targetReleaseDate || null,
            productOwnerId: backlogForm.productOwnerId,
            engineeringOwnerId: backlogForm.engineeringOwnerId || null,
          },
          ...refetch,
        });
        if (result?.engineeringRoadmapBacklogItemCreate?.status !== 'OK') {
          setBacklogFormError(result?.engineeringRoadmapBacklogItemCreate?.error ?? 'Create failed');
          setSubmitting(false);
          return;
        }
        const created = result.engineeringRoadmapBacklogItemCreate?.item;
        if (created?.id) {
          setBacklogPostCreateFocus({ id: created.id, segment: created.segment });
        }
      }
      closeBacklogModal();
    } catch (err: unknown) {
      setBacklogFormError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  }, [backlogForm, editingBacklogItem, createBacklogItem, updateBacklogItem, todayStr, closeBacklogModal]);

  const insertBriefTemplate = useCallback(() => {
    setBacklogForm((b) => {
      const t = BRIEF_TEMPLATE_BY_WORK_TYPE[b.workType];
      const next = b.briefBody.trim() ? `${b.briefBody.trim()}\n\n${t}` : t;
      return { ...b, briefBody: next };
    });
  }, []);

  const updateField = useCallback(<K extends keyof EntryFormData>(field: K, value: EntryFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDelete = useCallback(
    async (entry: { id: string }) => {
      if (
        !window.confirm(
          'Delete this roadmap entry? This cannot be undone. Any linked backlog row will stay, but the timeline bar will be removed.',
        )
      )
        return;
      try {
        const { data: result } = await destroyEntry({
          variables: { id: entry.id },
          ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
        });
        if (result?.engineeringRoadmapEntryDestroy?.status !== 'OK') {
          setFormError(result?.engineeringRoadmapEntryDestroy?.error ?? 'Failed to delete entry');
          return;
        }
        closeEntryModal();
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Failed to delete entry');
      }
    },
    [destroyEntry, todayStr, closeEntryModal],
  );

  const handleReorder = useCallback(
    async (
      direction: typeof EngineeringRoadmap__ReorderDirection.Up | typeof EngineeringRoadmap__ReorderDirection.Down,
    ) => {
      if (!editingEntry) return;
      const entry = 'entry' in editingEntry ? editingEntry.entry : editingEntry;
      setFormError(null);
      try {
        const { data: result } = await reorderEntry({
          variables: { id: entry.id, direction },
          ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
        });
        const payload = result?.engineeringRoadmapEntryReorder;
        if (payload?.status === 'UNPROCESSABLE' && payload.error) {
          setFormError(payload.error);
        } else if (payload?.status === 'OK' && editingQueueContext) {
          const newIndex =
            direction === EngineeringRoadmap__ReorderDirection.Up
              ? editingQueueContext.queueIndex - 1
              : editingQueueContext.queueIndex + 1;
          setEditingQueueContext({ ...editingQueueContext, queueIndex: newIndex });
        }
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Failed to reorder');
      }
    },
    [editingEntry, editingQueueContext, reorderEntry, todayStr],
  );

  const handleBacklogReorder = useCallback(
    async (item: BacklogItem, direction: EngineeringRoadmap__ReorderDirection) => {
      try {
        const { data: result } = await reorderBacklogItem({
          variables: { id: item.id, direction },
          ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
        });
        const payload = result?.engineeringRoadmapBacklogItemReorder;
        if (payload?.status === Status.Unprocessable && payload.error) {
          window.alert(payload.error);
        }
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Could not reorder.');
      }
    },
    [reorderBacklogItem, todayStr],
  );

  const handleBacklogComplete = useCallback(
    async (item: BacklogItem) => {
      try {
        const { data: result } = await updateBacklogItem({
          variables: {
            id: item.id,
            title: item.title,
            bucket: EngineeringRoadmap__BacklogBucket.Completed,
            workType: item.workType,
            briefBody: item.briefBody ?? null,
            prdUrl: item.prdURL ?? null,
            linearUrl: item.linearURL ?? null,
            targetReleaseDate: item.targetReleaseDate ?? null,
            productOwnerId: item.productOwner?.id ?? null,
            engineeringOwnerId: item.engineeringOwner?.id ?? null,
          },
          ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
        });
        const payload = result?.engineeringRoadmapBacklogItemUpdate;
        if (payload && payload.status !== Status.Ok) {
          window.alert(payload.error ?? 'Could not mark this item complete.');
        }
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Could not mark this item complete.');
      }
    },
    [updateBacklogItem, todayStr],
  );

  const handleBacklogUncomplete = useCallback(
    async (item: BacklogItem) => {
      try {
        await updateBacklogItem({
          variables: {
            id: item.id,
            title: item.title,
            bucket: EngineeringRoadmap__BacklogBucket.Active,
            workType: item.workType,
            briefBody: item.briefBody ?? null,
            prdUrl: item.prdURL ?? null,
            linearUrl: item.linearURL ?? null,
            targetReleaseDate: item.targetReleaseDate ?? null,
            productOwnerId: item.productOwner?.id ?? null,
            engineeringOwnerId: item.engineeringOwner?.id ?? null,
          },
          ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
        });
      } catch {
        /* ignore */
      }
    },
    [updateBacklogItem, todayStr],
  );

  const handleBacklogDestroy = useCallback(
    async (item: BacklogItem) => {
      if (
        !window.confirm(
          'Remove this backlog project? This cannot be undone. If it was pulled to the roadmap, the timeline entry is not deleted automatically.',
        )
      )
        return;
      try {
        await destroyBacklogItem({
          variables: { id: item.id },
          ...refetchRoadmapAndBacklogSheets(todayStr, backlogSheetRef.current, myWorkSheetRef.current),
        });
      } catch {
        /* ignore */
      }
    },
    [destroyBacklogItem, todayStr],
  );

  const rangeLabel = `${rangeStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${rangeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const renderBar = useCallback(
    (
      startDate: string,
      endDate: string,
      entry: {
        id: string;
        title: string;
        kind: string;
        segment?: EngineeringRoadmap__Segment | null;
        user: { id: string; name: string };
        note?: string | null;
        estimatedDurationDays?: number | null;
        bufferPercent?: number | null;
        status?: string | null;
        productOwner?: { name: string } | null;
        engineeringOwner?: { name: string } | null;
      },
      isTimelineItem: boolean,
      queueContext?: { queueIndex: number; queueLength: number },
    ) => {
      const start = parseDate(startDate);
      const end = parseDate(endDate);
      const clampedStart = start < rangeStart ? rangeStart : start;
      const clampedEnd = end > rangeEnd ? rangeEnd : end;
      const duration = diffDays(clampedStart, clampedEnd) + 1;
      // No intersection with the visible sprint window — do not render (avoids pinning past/future work to
      // the window edge, which looked like a 1-day bar at the start of every period when navigating).
      if (clampedStart > clampedEnd || duration <= 0) return null;

      const totalDays = dates.length;
      if (totalDays === 0) return null;

      const startOffset = diffDays(rangeStart, clampedStart);
      const leftPct = (startOffset / totalDays) * 100;
      const rawWidthPct = (duration / totalDays) * 100;
      const widthPct = Math.max(rawWidthPct - 0.3, 0.25);
      const isOoo = entry.kind === EngineeringRoadmap__Kind.OutOfOffice;
      const bg = isOoo
        ? OOO_BAR_BG
        : entry.segment
          ? SEGMENT_COLOR[entry.segment]
          : SEGMENT_COLOR[EngineeringRoadmap__Segment.Engineering];

      const durationTooltipLine =
        !isOoo &&
        entry.kind === EngineeringRoadmap__Kind.Project &&
        entry.estimatedDurationDays != null &&
        entry.estimatedDurationDays >= 1
          ? (() => {
              const core = entry.estimatedDurationDays!;
              const bp = entry.bufferPercent ?? 0;
              const buf = bufferDaysRoundedUp(core, bp);
              const total = core + buf;
              return `${total} working days on timeline (${core} core + ${buf} buffer at ${bp}%, rounded up)`;
            })()
          : entry.estimatedDurationDays
            ? `${entry.estimatedDurationDays} working days`
            : '';

      const tooltipText = [
        entry.title,
        !isOoo && entry.segment ? `Team: ${SEGMENT_LABELS[entry.segment]}` : null,
        `${startDate} – ${endDate}`,
        durationTooltipLine,
        entry.status ? `Status: ${entry.status}` : '',
        entry.productOwner ? `Product owner: ${entry.productOwner.name}` : null,
        entry.engineeringOwner ? `Engineering owner: ${entry.engineeringOwner.name}` : null,
        entry.note || '',
      ]
        .filter(Boolean)
        .join('\n');

      return (
        <EntryBar
          key={entry.id}
          type="button"
          leftPct={leftPct}
          widthPct={widthPct}
          bg={bg}
          aria-label={`Edit ${entry.title}`}
          onClick={() =>
            openEditModal(
              (isTimelineItem ? { entry, startDate, endDate } : (entry as Entry)) as Entry | TimelineItem,
              queueContext,
            )
          }
          onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, text: tooltipText })}
          onMouseLeave={() => setTooltip(null)}
        >
          {widthPct > 5 ? entry.title : ''}
        </EntryBar>
      );
    },
    [rangeStart, rangeEnd, dates, openEditModal],
  );

  const renderBacklogList = (
    rows: Array<{ item: BacklogItem; canMoveUp: boolean; canMoveDown: boolean }>,
    bucket: EngineeringRoadmap__BacklogBucket,
    reorderEnabled: boolean,
    showSegmentBadge: boolean,
  ) =>
    rows.map(({ item, canMoveUp: serverUp, canMoveDown: serverDown }) => {
      const canMoveUp = reorderEnabled && serverUp;
      const canMoveDown = reorderEnabled && serverDown;
      const onRoadmap = Boolean(item.roadmapEntry);
      const isCompletedBucket = bucket === EngineeringRoadmap__BacklogBucket.Completed;
      const pullDisabled = onRoadmap || isCompletedBucket;
      const pullTitle = onRoadmap
        ? 'Already linked to the engineer roadmap'
        : isCompletedBucket
          ? 'Completed items cannot be pulled to the roadmap'
          : undefined;
      const pullLabel = onRoadmap ? 'On roadmap' : isCompletedBucket ? 'Completed' : 'Pull to roadmap';
      const scheduleText = backlogRoadmapScheduleText(item, isCompletedBucket, timelineSlotByEntryId);
      const linkedStillPlanned =
        Boolean(item.roadmapEntry) && item.roadmapEntry!.status === EngineeringRoadmap__Status.Planned;
      const completeDisabled = bucket === EngineeringRoadmap__BacklogBucket.Active && linkedStillPlanned;
      const completeTitle = linkedStillPlanned
        ? 'Move the linked roadmap entry out of Planned (e.g. In progress, Ready for QA, or In QA) before completing.'
        : undefined;
      const reorderHintUp = 'Open the “All active” tab to change priority with the arrows (global list order).';
      const moveUpTitle =
        !reorderEnabled && bucket === EngineeringRoadmap__BacklogBucket.Active
          ? reorderHintUp
          : !canMoveUp && reorderEnabled && bucket === EngineeringRoadmap__BacklogBucket.Active
            ? 'Already first on the team list, or spec-ready ordering blocks moving up.'
            : !canMoveUp && reorderEnabled
              ? 'Already first on the team list.'
              : undefined;
      const moveDownTitle =
        !reorderEnabled && bucket === EngineeringRoadmap__BacklogBucket.Active
          ? reorderHintUp
          : !canMoveDown && reorderEnabled && bucket === EngineeringRoadmap__BacklogBucket.Active
            ? 'Already last on the team list, or spec-ready ordering blocks moving down.'
            : !canMoveDown && reorderEnabled
              ? 'Already last on the team list.'
              : undefined;
      const statusPhrase = onRoadmap ? roadmapEntryStatusPhrase(item.roadmapEntry!.status) : null;
      const prdHref = absoluteExternalUrl(item.prdURL);
      const linearHref = absoluteExternalUrl(item.linearURL);
      return (
        <BacklogRow
          key={item.id}
          data-backlog-item-id={item.id}
          $highlight={item.id === backlogHighlightId}
          $segment={item.segment}
        >
          <BacklogRowTitle title={item.title}>{item.title}</BacklogRowTitle>
          <BacklogRowDetails>
            <BacklogRowBadgeRow>
              {item.targetReleaseDate ? (
                <CriticalDateBadge>Critical: {formatShortIsoDate(item.targetReleaseDate)}</CriticalDateBadge>
              ) : null}
              <RoadmapStatusBadge $onRoadmap={onRoadmap}>
                {onRoadmap
                  ? `On roadmap · ${item.roadmapEntry!.user.name}${statusPhrase ? ` · ${statusPhrase}` : ''}`
                  : 'Not on roadmap'}
              </RoadmapStatusBadge>
              {scheduleText ? <RoadmapScheduleBadge>{scheduleText}</RoadmapScheduleBadge> : null}
              {showSegmentBadge ? (
                <SegmentTeamBadge $segment={item.segment}>{SEGMENT_LABELS[item.segment]}</SegmentTeamBadge>
              ) : null}
              <WorkTypeChip>{WORK_TYPE_LABELS[item.workType]}</WorkTypeChip>
              {item.specReady ? (
                <SpecReadyBadge>Spec ready</SpecReadyBadge>
              ) : (
                <MissingSpecBadge>Missing spec</MissingSpecBadge>
              )}
              {prdHref ? (
                <PrdOpenBadge href={prdHref} target="_blank" rel="noopener noreferrer" title={prdHref}>
                  PRD ↗
                </PrdOpenBadge>
              ) : null}
              {linearHref ? (
                <LinearOpenBadge href={linearHref} target="_blank" rel="noopener noreferrer" title={linearHref}>
                  Linear ↗
                </LinearOpenBadge>
              ) : null}
            </BacklogRowBadgeRow>
            <OwnerMeta>
              <span style={{ color: '#94a3b8' }}>PO</span> {item.productOwner?.name ?? '—'}
              <span style={{ margin: '0 6px', color: '#cbd5e1' }}>|</span>
              <span style={{ color: '#94a3b8' }}>EO</span> {item.engineeringOwner?.name ?? '—'}
            </OwnerMeta>
          </BacklogRowDetails>
          <BacklogRowActions>
            <SmallButton
              type="button"
              disabled={!canMoveUp}
              title={moveUpTitle}
              onClick={() => handleBacklogReorder(item, EngineeringRoadmap__ReorderDirection.Up)}
            >
              ↑
            </SmallButton>
            <SmallButton
              type="button"
              disabled={!canMoveDown}
              title={moveDownTitle}
              onClick={() => handleBacklogReorder(item, EngineeringRoadmap__ReorderDirection.Down)}
            >
              ↓
            </SmallButton>
            <SmallButton
              type="button"
              disabled={pullDisabled}
              title={pullTitle}
              onClick={() => openCreateModal(undefined, false, item)}
            >
              {pullLabel}
            </SmallButton>
            {item.specReady ? (
              <SmallButton
                type="button"
                title="Copy spec + links for an AI assistant (only when spec-ready)"
                onClick={() =>
                  void copyTextToClipboard(
                    buildCopyAiPromptForSpecReadyBacklog({
                      title: item.title,
                      segment: item.segment,
                      workType: item.workType,
                      briefBody: item.briefBody ?? '',
                      prdUrl: item.prdURL ?? '',
                      linearUrl: item.linearURL ?? '',
                    }),
                  )
                }
              >
                Copy AI prompt
              </SmallButton>
            ) : null}
            <SmallButton type="button" onClick={() => openBacklogModal(item.segment, item)}>
              Edit
            </SmallButton>
            {bucket === EngineeringRoadmap__BacklogBucket.Active ? (
              <SmallButton
                type="button"
                disabled={completeDisabled}
                title={completeTitle}
                onClick={() => handleBacklogComplete(item)}
              >
                Complete
              </SmallButton>
            ) : (
              <SmallButton type="button" onClick={() => handleBacklogUncomplete(item)}>
                Reopen
              </SmallButton>
            )}
            <SmallButton type="button" onClick={() => handleBacklogDestroy(item)}>
              Remove
            </SmallButton>
          </BacklogRowActions>
        </BacklogRow>
      );
    });

  const compactEntryForm = Boolean(editingQueueContext);

  const backlogItemsPagePayload =
    teamBacklogBrowserSegment !== null ? backlogPageData?.engineeringRoadmapBacklogItemsPage : undefined;
  const backlogSheetRows = backlogItemsPagePayload?.rows ?? [];
  const backlogSheetTotalCount = backlogItemsPagePayload?.totalCount ?? 0;
  const backlogSheetTotalPages = backlogItemsPagePayload?.totalPages ?? 1;
  const backlogSheetResolvedPage = backlogItemsPagePayload?.page ?? teamBacklogPage;
  const backlogSheetReorderEnabled = tabAllowsReorder(teamBacklogTab);

  const myBacklogItemsPagePayload = myWorkBrowserOpen
    ? myBacklogPageData?.engineeringRoadmapMyBacklogItemsPage
    : undefined;
  const myWorkSheetRows = myBacklogItemsPagePayload?.rows ?? [];
  const myWorkSheetTotalCount = myBacklogItemsPagePayload?.totalCount ?? 0;
  const myWorkSheetTotalPages = myBacklogItemsPagePayload?.totalPages ?? 1;
  const myWorkSheetResolvedPage = myBacklogItemsPagePayload?.page ?? myWorkPage;
  const myWorkSheetReorderEnabled = false;

  return (
    <PageContainer>
      <Header>
        <div>
          <Title>Engineering Capacity Planner</Title>
          <DateRangeDisplay>{rangeLabel}</DateRangeDisplay>
        </div>
        <Controls>
          <IconButton onClick={openSettingsModal} title="Sprint settings">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
            </svg>
          </IconButton>
          <NavButton onClick={() => navigate('back')}>← Previous period</NavButton>
          <NavButton onClick={() => navigate('forward')}>Next period →</NavButton>
          <NavButton onClick={snapToCurrentSprint}>Current sprint</NavButton>
          <PrimaryButton onClick={() => openCreateModal()} disabled={users.length === 0}>
            + Add out of office
          </PrimaryButton>
        </Controls>
      </Header>

      <Legend>
        {SEGMENT_ORDER.map((seg) => (
          <LegendItem key={seg}>
            <LegendSwatch c={SEGMENT_COLOR[seg]} />
            {SEGMENT_LABELS[seg]}
          </LegendItem>
        ))}
        <LegendItem>
          <div style={{ width: 14, height: 12, background: OOO_BAR_BG, borderRadius: 3 }} />
          Out of office
        </LegendItem>
        <LegendItem>
          <div style={{ width: 2, height: 12, background: '#ef4444', borderRadius: 1 }} />
          Today
        </LegendItem>
      </Legend>

      {error && <ErrorBanner>Failed to load roadmap data. Please try refreshing.</ErrorBanner>}

      {loading && !data ? (
        <EmptyState>Loading...</EmptyState>
      ) : (
        <>
          {users.length === 0 ? (
            <EmptyState>
              No engineers found. Assign the <strong>engineering</strong> role to users to see them here.
            </EmptyState>
          ) : (
            <>
              <SectionHeading>Engineer roadmap</SectionHeading>
              <TimelineWrapper>
                <TimelineChrome>
                  <HeaderRow>
                    <EngineerCorner>
                      <div
                        style={{
                          padding: '8px 14px',
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#9ca3af',
                          textTransform: 'uppercase' as const,
                          letterSpacing: 0.5,
                        }}
                      >
                        Engineer
                      </div>
                    </EngineerCorner>
                    <TimelineHeaderBlock>
                      <div style={{ display: 'flex', width: '100%', borderBottom: '2px solid #e5e7eb' }}>
                        {months.map((m, i) => (
                          <MonthHeaderCell key={i} $flex={m.span}>
                            {m.label}
                          </MonthHeaderCell>
                        ))}
                      </div>
                      <div style={{ display: 'flex', width: '100%', borderBottom: '1px solid #e5e7eb' }}>
                        {dates.map((d, i) => {
                          const dow = d.getDay();
                          const isWeekend = dow === 0 || dow === 6;
                          const isToday = formatDate(d) === todayStr;
                          const sprintStripe = sprintStripeIndex(refStart, cadenceDays, d);
                          return (
                            <DayCell key={i} isWeekend={isWeekend} isToday={isToday} sprintStripe={sprintStripe}>
                              <div>{['S', 'M', 'T', 'W', 'T', 'F', 'S'][dow]}</div>
                              <div style={{ fontWeight: 500 }}>{d.getDate()}</div>
                            </DayCell>
                          );
                        })}
                      </div>
                    </TimelineHeaderBlock>
                  </HeaderRow>

                  {timeline.map((userTimeline, idx) => {
                    const user = userTimeline.user;
                    const oooItems = oooByUser.get(user.id) ?? [];
                    const initials = user.name
                      .split(' ')
                      .map((w: string) => w[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase();

                    return (
                      <RowContainer key={user.id} isEven={idx % 2 === 0}>
                        <UserLabel
                          type="button"
                          onClick={() => openCreateModal(user.id)}
                          aria-label={`Add entry for ${user.name}`}
                        >
                          <Avatar>{initials}</Avatar>
                          <span>{user.name}</span>
                        </UserLabel>
                        <BarArea>
                          {todayLeftPct >= 0 && <TodayLine leftPct={todayLeftPct} />}
                          {userTimeline.queueItems.map((item, qIdx) =>
                            renderBar(item.startDate, item.endDate, item.entry, true, {
                              queueIndex: qIdx,
                              queueLength: userTimeline.queueItems.length,
                            }),
                          )}
                          {oooItems.map((e) =>
                            e.startDate && e.endDate ? renderBar(e.startDate, e.endDate, e, false) : null,
                          )}
                        </BarArea>
                      </RowContainer>
                    );
                  })}
                </TimelineChrome>
              </TimelineWrapper>
            </>
          )}

          <BacklogSection style={{ marginTop: users.length === 0 ? 0 : 28 }}>
            <BacklogBody>
              <SectionHeading style={{ marginBottom: 4 }}>My work</SectionHeading>
              <FormHint style={{ marginBottom: 12 }}>
                Rows where you are <strong>product owner</strong>, <strong>engineering owner</strong>, or the{' '}
                <strong>roadmap assignee</strong> for a linked backlog item — all teams in one place. Same tabs and
                paging as team sheets; ↑ / ↓ stay off (priority order lives on each team’s <strong>All active</strong>{' '}
                list).
              </FormHint>
              <div style={{ marginBottom: 8 }}>
                <TeamBacklogSummaryRow
                  type="button"
                  $segment={EngineeringRoadmap__Segment.Engineering}
                  onClick={openMyWorkBrowser}
                >
                  <span>
                    Open my backlog sheet
                    {myWorkSummary ? (
                      <>
                        <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>
                          ({myWorkSummary.activeCount} active
                        </span>
                        {myWorkSummary.completedCount > 0 ? (
                          <span style={{ fontWeight: 400, color: '#6b7280' }}>
                            , {myWorkSummary.completedCount} done
                          </span>
                        ) : null}
                        <span style={{ fontWeight: 400, color: '#6b7280' }}>)</span>
                        {myWorkSummary.activeMissingSpecCount > 0 ? (
                          <span style={{ fontWeight: 500, color: '#b45309', marginLeft: 8 }}>
                            · {myWorkSummary.activeMissingSpecCount} active missing spec
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>(…)</span>
                    )}
                  </span>
                  <span style={{ color: '#64748b', fontWeight: 600, fontSize: 13 }}>Open →</span>
                </TeamBacklogSummaryRow>
              </div>
            </BacklogBody>
          </BacklogSection>

          <BacklogSection style={{ marginTop: 20 }}>
            <BacklogBody>
              <SectionHeading style={{ marginBottom: 4 }}>Team backlogs</SectionHeading>
              <FormHint style={{ marginBottom: 16 }}>
                Open a team for a full-page sheet with <strong>tabs</strong> (active, filters, done) and{' '}
                <strong>server-side paging</strong>. Use the <strong>All active</strong> tab to reorder with ↑ / ↓
                (global priority). <strong>Create project</strong> appends to the <strong>end</strong> of that team’s
                active backlog (lowest priority / largest position) until you move it; after you create one, we open
                that team’s sheet to the last page and scroll to the new row.
              </FormHint>
              <FormHint
                style={{
                  marginBottom: 16,
                  color: '#92400e',
                  background: '#fffbeb',
                  padding: '10px 12px',
                  borderRadius: 8,
                }}
              >
                <strong>Spec before priority.</strong> A row is <strong>spec-ready</strong> when it has a PRD link{' '}
                <em>or</em> an in-app brief of at least {SPEC_READY_MIN_BRIEF_CHARS} characters (markdown is fine — use
                templates in the form or paste from an AI session). That is enough for humans and AI to verify when work
                is done. On <strong>All active</strong>, spec-ready items stay above rows still missing spec — add a PRD
                or brief to move up.
              </FormHint>
              <FormHint
                style={{
                  marginBottom: 16,
                  color: '#92400e',
                  background: '#fffbeb',
                  padding: '10px 12px',
                  borderRadius: 8,
                }}
              >
                <strong>Pull to roadmap.</strong> Engineers should be the ones pulling work onto the engineer roadmap
                when possible. A project should <strong>not</strong> be pulled to the roadmap without talking to the{' '}
                <strong>engineering team first</strong> — so the right engineer, queue order, and timing match real
                capacity and priorities.
              </FormHint>
              {SEGMENT_ORDER.map((seg) => {
                const sums = summaryBySegment.get(seg) ?? {
                  activeCount: 0,
                  completedCount: 0,
                  activeMissingSpecCount: 0,
                };
                const { activeCount, completedCount, activeMissingSpecCount } = sums;
                return (
                  <div key={seg} style={{ marginBottom: 12 }}>
                    <TeamBacklogSummaryRow type="button" $segment={seg} onClick={() => openTeamBacklogBrowser(seg)}>
                      <span>
                        {SEGMENT_LABELS[seg]}
                        <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>({activeCount} active</span>
                        {completedCount > 0 ? (
                          <span style={{ fontWeight: 400, color: '#6b7280' }}>, {completedCount} done</span>
                        ) : null}
                        <span style={{ fontWeight: 400, color: '#6b7280' }}>)</span>
                        {activeMissingSpecCount > 0 ? (
                          <span style={{ fontWeight: 500, color: '#b45309', marginLeft: 8 }}>
                            · {activeMissingSpecCount} active missing spec
                          </span>
                        ) : null}
                      </span>
                      <span style={{ color: '#64748b', fontWeight: 600, fontSize: 13 }}>Open backlog →</span>
                    </TeamBacklogSummaryRow>
                  </div>
                );
              })}
            </BacklogBody>
          </BacklogSection>
        </>
      )}

      {tooltip && <Tooltip style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}>{tooltip.text}</Tooltip>}

      {teamBacklogBrowserSegment !== null ? (
        <TeamBacklogBrowserOverlay onClick={closeTeamBacklogBrowser} role="presentation">
          <TeamBacklogBrowserSheet
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`team-backlog-title-${teamBacklogBrowserSegment}`}
          >
            <TeamBacklogBrowserHeader>
              <TeamBacklogBrowserTitleBlock>
                <TeamBacklogBrowserTitle id={`team-backlog-title-${teamBacklogBrowserSegment}`}>
                  {SEGMENT_LABELS[teamBacklogBrowserSegment]} backlog
                </TeamBacklogBrowserTitle>
                <TeamBacklogBrowserMeta>
                  {(() => {
                    const sums = summaryBySegment.get(teamBacklogBrowserSegment) ?? {
                      activeCount: 0,
                      completedCount: 0,
                      activeMissingSpecCount: 0,
                    };
                    return (
                      <>
                        {sums.activeCount} active
                        {sums.completedCount > 0 ? ` · ${sums.completedCount} done` : ''}
                      </>
                    );
                  })()}
                </TeamBacklogBrowserMeta>
              </TeamBacklogBrowserTitleBlock>
              <TeamBacklogBrowserHeaderActions>
                <SmallButton type="button" onClick={() => openBacklogModal(teamBacklogBrowserSegment, null)}>
                  + Add project
                </SmallButton>
                <NavButton type="button" onClick={closeTeamBacklogBrowser}>
                  Close
                </NavButton>
              </TeamBacklogBrowserHeaderActions>
            </TeamBacklogBrowserHeader>
            <TeamBacklogBrowserBody>
              <TeamBacklogBrowserHint>
                Top of the <strong>All active</strong> list = higher priority. On other tabs, ↑ / ↓ are disabled —
                switch to All active to change order. Spec-ready rules still apply when reordering.
              </TeamBacklogBrowserHint>
              <TeamBacklogTabBar role="tablist" aria-label="Backlog views">
                {(
                  [
                    { id: 'active_all' as const, label: 'All active' },
                    { id: 'active_missing_prd' as const, label: 'Missing spec' },
                    { id: 'active_on_roadmap' as const, label: 'On roadmap' },
                    { id: 'active_planned' as const, label: 'Queued (planned)' },
                    { id: 'active_in_progress' as const, label: 'In progress' },
                    { id: 'active_ready_for_qa' as const, label: 'Ready for QA' },
                    { id: 'active_in_qa' as const, label: 'In QA' },
                    { id: 'done' as const, label: 'Done' },
                  ] as const
                ).map(({ id, label }) => (
                  <TeamBacklogTab
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={teamBacklogTab === id}
                    $active={teamBacklogTab === id}
                    onClick={() => selectTeamBacklogTab(id)}
                  >
                    {label}
                  </TeamBacklogTab>
                ))}
              </TeamBacklogTabBar>
              {backlogPageError ? (
                <ErrorBanner>Could not load this backlog page. Try again or close and reopen.</ErrorBanner>
              ) : null}
              {backlogPageLoading && backlogSheetRows.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>
              ) : backlogSheetTotalCount === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No projects in this view.</div>
              ) : (
                <>
                  {renderBacklogList(backlogSheetRows, sheetBucket, backlogSheetReorderEnabled, false)}
                  <TeamBacklogPager>
                    <span>
                      Page {backlogSheetResolvedPage} of {backlogSheetTotalPages} · {backlogSheetTotalCount} total
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <NavButton
                        type="button"
                        disabled={backlogSheetResolvedPage <= 1 || backlogPageLoading}
                        onClick={() => setTeamBacklogPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </NavButton>
                      <NavButton
                        type="button"
                        disabled={backlogSheetResolvedPage >= backlogSheetTotalPages || backlogPageLoading}
                        onClick={() => setTeamBacklogPage((p) => p + 1)}
                      >
                        Next
                      </NavButton>
                    </div>
                  </TeamBacklogPager>
                </>
              )}
            </TeamBacklogBrowserBody>
          </TeamBacklogBrowserSheet>
        </TeamBacklogBrowserOverlay>
      ) : null}

      {myWorkBrowserOpen ? (
        <TeamBacklogBrowserOverlay onClick={closeMyWorkBrowser} role="presentation">
          <TeamBacklogBrowserSheet
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="my-work-backlog-title"
          >
            <TeamBacklogBrowserHeader>
              <TeamBacklogBrowserTitleBlock>
                <TeamBacklogBrowserTitle id="my-work-backlog-title">My work</TeamBacklogBrowserTitle>
                <TeamBacklogBrowserMeta>
                  {myWorkSummary ? (
                    <>
                      {myWorkSummary.activeCount} active
                      {myWorkSummary.completedCount > 0 ? ` · ${myWorkSummary.completedCount} done` : ''}
                    </>
                  ) : null}
                </TeamBacklogBrowserMeta>
              </TeamBacklogBrowserTitleBlock>
              <TeamBacklogBrowserHeaderActions>
                <SmallButton
                  type="button"
                  onClick={() => openBacklogModal(EngineeringRoadmap__Segment.Engineering, null)}
                >
                  + Add project
                </SmallButton>
                <NavButton type="button" onClick={closeMyWorkBrowser}>
                  Close
                </NavButton>
              </TeamBacklogBrowserHeaderActions>
            </TeamBacklogBrowserHeader>
            <TeamBacklogBrowserBody>
              <TeamBacklogBrowserHint>
                Cross-team list sorted by <strong>team</strong> then <strong>priority</strong>. Reorder on each team’s{' '}
                <strong>All active</strong> sheet; use this view to filter and triage what’s yours.
              </TeamBacklogBrowserHint>
              <TeamBacklogTabBar role="tablist" aria-label="My work backlog views">
                {(
                  [
                    { id: 'active_all' as const, label: 'All active' },
                    { id: 'active_missing_prd' as const, label: 'Missing spec' },
                    { id: 'active_on_roadmap' as const, label: 'On roadmap' },
                    { id: 'active_planned' as const, label: 'Queued (planned)' },
                    { id: 'active_in_progress' as const, label: 'In progress' },
                    { id: 'active_ready_for_qa' as const, label: 'Ready for QA' },
                    { id: 'active_in_qa' as const, label: 'In QA' },
                    { id: 'done' as const, label: 'Done' },
                  ] as const
                ).map(({ id, label }) => (
                  <TeamBacklogTab
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={myWorkTab === id}
                    $active={myWorkTab === id}
                    onClick={() => selectMyWorkTab(id)}
                  >
                    {label}
                  </TeamBacklogTab>
                ))}
              </TeamBacklogTabBar>
              {myBacklogPageError ? (
                <ErrorBanner>Could not load this page. Try again or close and reopen.</ErrorBanner>
              ) : null}
              {myBacklogPageLoading && myWorkSheetRows.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>
              ) : myWorkSheetTotalCount === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No projects in this view.</div>
              ) : (
                <>
                  {renderBacklogList(myWorkSheetRows, myWorkBucket, myWorkSheetReorderEnabled, true)}
                  <TeamBacklogPager>
                    <span>
                      Page {myWorkSheetResolvedPage} of {myWorkSheetTotalPages} · {myWorkSheetTotalCount} total
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <NavButton
                        type="button"
                        disabled={myWorkSheetResolvedPage <= 1 || myBacklogPageLoading}
                        onClick={() => setMyWorkPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </NavButton>
                      <NavButton
                        type="button"
                        disabled={myWorkSheetResolvedPage >= myWorkSheetTotalPages || myBacklogPageLoading}
                        onClick={() => setMyWorkPage((p) => p + 1)}
                      >
                        Next
                      </NavButton>
                    </div>
                  </TeamBacklogPager>
                </>
              )}
            </TeamBacklogBrowserBody>
          </TeamBacklogBrowserSheet>
        </TeamBacklogBrowserOverlay>
      ) : null}

      {settingsModalOpen && (
        <ModalOverlay onClick={() => setSettingsModalOpen(false)} role="presentation">
          <ScrollModalContent
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-modal-title"
          >
            <ScrollModalHeader>
              <ModalTitle id="settings-modal-title">Sprint Settings</ModalTitle>
              {settingsError && <ErrorBanner>{settingsError}</ErrorBanner>}
            </ScrollModalHeader>
            <ScrollModalBody>
              <FormGroup>
                <FormLabel htmlFor="roadmap-settings-reference-start-date">Reference sprint start date</FormLabel>
                <FormInput
                  id="roadmap-settings-reference-start-date"
                  type="date"
                  value={settingsForm.referenceStartDate}
                  onChange={(e) => setSettingsForm((s) => ({ ...s, referenceStartDate: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="roadmap-settings-cadence-days">Sprint cadence (days)</FormLabel>
                <FormInput
                  id="roadmap-settings-cadence-days"
                  type="number"
                  min={1}
                  value={settingsForm.cadenceDays}
                  onChange={(e) => setSettingsForm((s) => ({ ...s, cadenceDays: parseInt(e.target.value, 10) || 14 }))}
                />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>e.g. 14 for 2-week sprints</div>
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="roadmap-settings-default-buffer-percent">Default buffer (%)</FormLabel>
                <FormInput
                  id="roadmap-settings-default-buffer-percent"
                  type="number"
                  min={0}
                  max={100}
                  value={settingsForm.defaultBufferPercent}
                  onChange={(e) =>
                    setSettingsForm((s) => ({
                      ...s,
                      defaultBufferPercent: Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)),
                    }))
                  }
                />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  Default for new roadmap projects (meetings, bugs, pairing, ad hoc). Each project can override. Buffer
                  rounds up to whole working days.
                </div>
              </FormGroup>
            </ScrollModalBody>
            <ScrollModalFooter>
              <ScrollModalActions>
                <NavButton onClick={() => setSettingsModalOpen(false)}>Cancel</NavButton>
                <PrimaryButton onClick={saveSettings} disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save'}
                </PrimaryButton>
              </ScrollModalActions>
            </ScrollModalFooter>
          </ScrollModalContent>
        </ModalOverlay>
      )}

      {backlogModalOpen && (
        <ModalOverlay onClick={closeBacklogModal} role="presentation">
          <ScrollModalContent
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="backlog-modal-title"
          >
            <ScrollModalHeader>
              <ModalTitle id="backlog-modal-title">
                {editingBacklogItem ? 'Edit backlog project' : 'Add backlog project'}
              </ModalTitle>
              {backlogFormError && <ErrorBanner>{backlogFormError}</ErrorBanner>}
            </ScrollModalHeader>
            <ScrollModalBody>
              <FormGroup>
                <FormLabel htmlFor="backlog-segment">Team</FormLabel>
                <FormSelect
                  id="backlog-segment"
                  value={backlogForm.segment}
                  disabled={!!editingBacklogItem}
                  onChange={(e) =>
                    setBacklogForm((b) => ({ ...b, segment: e.target.value as EngineeringRoadmap__Segment }))
                  }
                >
                  {SEGMENT_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {SEGMENT_LABELS[s]}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-title">Title</FormLabel>
                <FormInput
                  id="backlog-title"
                  value={backlogForm.title}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, title: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-work-type">Work type</FormLabel>
                <FormSelect
                  id="backlog-work-type"
                  value={backlogForm.workType}
                  onChange={(e) =>
                    setBacklogForm((b) => ({ ...b, workType: e.target.value as EngineeringRoadmap__BacklogWorkType }))
                  }
                >
                  {BACKLOG_WORK_TYPE_OPTIONS.map((wt) => (
                    <option key={wt} value={wt}>
                      {WORK_TYPE_LABELS[wt]}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-brief">In-app brief (markdown)</FormLabel>
                <FormHint>
                  Optional at intake. For <strong>spec-ready</strong> without a PRD, use at least{' '}
                  {SPEC_READY_MIN_BRIEF_CHARS} characters — or link a PRD below. <strong>Insert template</strong> gives
                  an AI-friendly outline (voice dictation, context, Figma/screenshot links). After the spec is ready,
                  use <strong>Copy AI prompt</strong> on the backlog row (not here).
                </FormHint>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <SmallButton type="button" onClick={insertBriefTemplate}>
                    Insert template
                  </SmallButton>
                </div>
                <FormTextarea
                  id="backlog-brief"
                  rows={10}
                  value={backlogForm.briefBody}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, briefBody: e.target.value }))}
                  style={{ marginTop: 8, minHeight: 160 }}
                />
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {backlogForm.briefBody.trim().length} / {SPEC_READY_MIN_BRIEF_CHARS}+ chars for spec-ready (unless PRD
                  is set)
                </div>
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-po">Product owner</FormLabel>
                <FormSelect
                  id="backlog-po"
                  value={backlogForm.productOwnerId}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, productOwnerId: e.target.value }))}
                >
                  {!editingBacklogItem && <option value="">Select product owner</option>}
                  {productOwners.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-prd">PRD / doc link</FormLabel>
                <FormHint>
                  Link a doc when you have one — or rely on the in-app brief above. Use a full URL (e.g. Google Docs);
                  if you omit <code>https://</code>, it will be added when you save. Either the PRD or the brief should
                  spell out <strong>goals</strong> and <strong>success criteria</strong> clearly enough to verify when
                  work is complete.
                </FormHint>
                <FormInput
                  id="backlog-prd"
                  type="text"
                  placeholder="https://..."
                  value={backlogForm.prdUrl}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, prdUrl: e.target.value }))}
                  style={{ marginTop: 8 }}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-linear">Linear</FormLabel>
                <FormHint>Optional — link to a Linear issue, project, or view for this work.</FormHint>
                <FormInput
                  id="backlog-linear"
                  type="text"
                  placeholder="https://linear.app/..."
                  value={backlogForm.linearUrl}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, linearUrl: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-target">Target release date</FormLabel>
                <FormHint>
                  Only for <strong>critical</strong> hard deadlines (e.g. contractual or launch-locked dates). Otherwise
                  leave this blank and set priority by reordering projects — work is scheduled as fast as capacity
                  allows.
                </FormHint>
                <FormInput
                  id="backlog-target"
                  type="date"
                  value={backlogForm.targetReleaseDate}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, targetReleaseDate: e.target.value }))}
                  style={{ marginTop: 8 }}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="backlog-eo">Engineering owner</FormLabel>
                <FormHint>
                  <strong>Leave blank when creating</strong> a project unless you have already spoken with that engineer
                  and they expect to own the work. Otherwise the engineering team will decide who the engineering owner
                  is. When someone is set here, they are selected by default for <strong>Pull to roadmap</strong>; you
                  can still choose a different engineer.
                </FormHint>
                <FormSelect
                  id="backlog-eo"
                  value={backlogForm.engineeringOwnerId}
                  onChange={(e) => setBacklogForm((b) => ({ ...b, engineeringOwnerId: e.target.value }))}
                  style={{ marginTop: 8 }}
                >
                  <option value="">—</option>
                  {engineeringOwners.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
            </ScrollModalBody>
            <ScrollModalFooter>
              <ScrollModalActions>
                <NavButton onClick={closeBacklogModal}>Cancel</NavButton>
                <PrimaryButton onClick={handleBacklogSubmit} disabled={submitting}>
                  {submitting ? 'Saving...' : editingBacklogItem ? 'Update' : 'Create'}
                </PrimaryButton>
              </ScrollModalActions>
            </ScrollModalFooter>
          </ScrollModalContent>
        </ModalOverlay>
      )}

      {entryModalOpen && (
        <ModalOverlay onClick={closeEntryModal} role="presentation">
          <ScrollModalContent
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="entry-modal-title"
          >
            <ScrollModalHeader>
              <ModalTitle id="entry-modal-title">
                {editingEntry
                  ? 'Edit roadmap entry'
                  : pendingBacklogItemId
                    ? 'Pull backlog to roadmap'
                    : 'Add out of office'}
              </ModalTitle>
              {formError && <ErrorBanner>{formError}</ErrorBanner>}
            </ScrollModalHeader>

            <ScrollModalBody>
              {!editingEntry && (
                <FormGroup>
                  <FormLabel htmlFor="roadmap-entry-user-id">Engineer</FormLabel>
                  <FormSelect
                    id="roadmap-entry-user-id"
                    value={formData.userID}
                    onChange={(e) => updateField('userID', e.target.value)}
                  >
                    {users.map((u: RoadmapUser) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>
              )}

              {editingEntry ? (
                <FormGroup>
                  <FormLabel htmlFor="roadmap-entry-kind">Type</FormLabel>
                  <FormSelect
                    id="roadmap-entry-kind"
                    value={formData.kind}
                    onChange={(e) => updateField('kind', e.target.value)}
                  >
                    <option value={EngineeringRoadmap__Kind.Project}>Project</option>
                    <option value={EngineeringRoadmap__Kind.OutOfOffice}>Out of Office</option>
                  </FormSelect>
                </FormGroup>
              ) : null}
              {!editingEntry && pendingBacklogItemId ? (
                <FormGroup>
                  <FormHint style={{ marginBottom: 0 }}>
                    New <strong>project</strong> bars must come from a backlog row (this flow). Use{' '}
                    <strong>+ Add out of office</strong> in the page header for vacation or other time off.
                  </FormHint>
                </FormGroup>
              ) : null}

              {formData.kind === EngineeringRoadmap__Kind.Project && (
                <>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-segment">Team / segment</FormLabel>
                    <FormSelect
                      id="roadmap-entry-segment"
                      value={formData.segment}
                      onChange={(e) => updateField('segment', e.target.value as EngineeringRoadmap__Segment)}
                    >
                      {SEGMENT_ORDER.map((s) => (
                        <option key={s} value={s}>
                          {SEGMENT_LABELS[s]}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-po">Product owner</FormLabel>
                    <FormSelect
                      id="roadmap-entry-po"
                      value={formData.productOwnerId}
                      onChange={(e) => updateField('productOwnerId', e.target.value)}
                    >
                      <option value="">—</option>
                      {productOwners.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-eo">Engineering owner</FormLabel>
                    <FormSelect
                      id="roadmap-entry-eo"
                      value={formData.engineeringOwnerId}
                      onChange={(e) => updateField('engineeringOwnerId', e.target.value)}
                    >
                      <option value="">—</option>
                      {engineeringOwners.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <FormLabel htmlFor="roadmap-entry-title">Title</FormLabel>
                <FormInput
                  id="roadmap-entry-title"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder={
                    formData.kind === EngineeringRoadmap__Kind.OutOfOffice
                      ? 'Defaults to "Out of Office"'
                      : 'e.g. Payment System Refactor'
                  }
                />
              </FormGroup>

              {formData.kind === EngineeringRoadmap__Kind.OutOfOffice ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-start-date">Start Date</FormLabel>
                    <FormInput
                      id="roadmap-entry-start-date"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => updateField('startDate', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-end-date">End Date</FormLabel>
                    <FormInput
                      id="roadmap-entry-end-date"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => updateField('endDate', e.target.value)}
                    />
                  </FormGroup>
                </div>
              ) : (
                <>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-estimated-duration">Estimated working days (core)</FormLabel>
                    <FormHint>
                      {compactEntryForm
                        ? 'Working days for the project, before buffer.'
                        : 'Working days for the project itself, before buffer.'}
                    </FormHint>
                    <FormInput
                      id="roadmap-entry-estimated-duration"
                      type="number"
                      min={1}
                      value={formData.estimatedDurationDays === '' ? '' : formData.estimatedDurationDays}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateField('estimatedDurationDays', v === '' ? '' : Math.max(1, parseInt(v, 10) || 1));
                      }}
                      placeholder="e.g. 5"
                      style={{ marginTop: 8 }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-buffer-percent">Buffer (%)</FormLabel>
                    <FormHint>
                      {compactEntryForm ? (
                        <>
                          Google Chat for meetings, bugs, and ad hoc work. Timeline uses <strong>core + buffer</strong>{' '}
                          (rounded up to whole days). Use <strong>0</strong> for none.
                        </>
                      ) : (
                        <>
                          Google Chat for meetings, bugs, pairing, reviews, and ad hoc work — not extra feature time.
                          The timeline uses <strong>core days + buffer</strong>, where buffer is{' '}
                          <strong>rounded up to whole working days</strong> (e.g. 1 core day at 20% → +1 buffer day).
                          Set to <strong>0</strong> for no buffer.
                        </>
                      )}
                    </FormHint>
                    <FormInput
                      id="roadmap-entry-buffer-percent"
                      type="number"
                      min={0}
                      max={100}
                      value={formData.bufferPercent}
                      onChange={(e) =>
                        updateField('bufferPercent', Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)))
                      }
                      style={{ marginTop: 8 }}
                    />
                    {typeof formData.estimatedDurationDays === 'number' && formData.estimatedDurationDays >= 1 ? (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                        Scheduled span:{' '}
                        <strong>
                          {scheduledTimelineWorkingDays(formData.estimatedDurationDays, formData.bufferPercent)} working
                          days
                        </strong>{' '}
                        ({formData.estimatedDurationDays} core +{' '}
                        {bufferDaysRoundedUp(formData.estimatedDurationDays, formData.bufferPercent)} buffer)
                      </div>
                    ) : null}
                  </FormGroup>
                </>
              )}

              {formData.kind !== EngineeringRoadmap__Kind.OutOfOffice &&
                editingEntry &&
                formData.status !== EngineeringRoadmap__Status.Planned && (
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-actual-start-date">Started on</FormLabel>
                    <FormInput
                      id="roadmap-entry-actual-start-date"
                      type="date"
                      value={formData.actualStartDate}
                      onChange={(e) => updateField('actualStartDate', e.target.value)}
                    />
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                      When you actually began this work. Used to preserve history when you change duration.
                    </div>
                  </FormGroup>
                )}

              {formData.kind !== EngineeringRoadmap__Kind.OutOfOffice &&
                editingEntry &&
                formData.status === EngineeringRoadmap__Status.Done && (
                  <FormGroup>
                    <FormLabel htmlFor="roadmap-entry-actual-end-date">Ended on</FormLabel>
                    <FormInput
                      id="roadmap-entry-actual-end-date"
                      type="date"
                      value={formData.actualEndDate}
                      onChange={(e) => updateField('actualEndDate', e.target.value)}
                    />
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                      When work actually completed. Used to preserve history.
                    </div>
                  </FormGroup>
                )}

              {formData.kind !== EngineeringRoadmap__Kind.OutOfOffice && editingEntry && (
                <FormGroup>
                  <FormLabel htmlFor="roadmap-entry-status">Status</FormLabel>
                  <FormSelect
                    id="roadmap-entry-status"
                    value={formData.status}
                    onChange={(e) => updateField('status', e.target.value)}
                  >
                    <option value={EngineeringRoadmap__Status.Planned}>Planned</option>
                    <option value={EngineeringRoadmap__Status.InProgress}>In progress</option>
                    <option value={EngineeringRoadmap__Status.ReadyForQa}>Ready for QA</option>
                    <option value={EngineeringRoadmap__Status.InQa}>In QA</option>
                    <option value={EngineeringRoadmap__Status.Done}>Done</option>
                  </FormSelect>
                </FormGroup>
              )}

              {editingQueueContext && (
                <FormGroup style={{ marginBottom: compactEntryForm ? 10 : 14 }}>
                  <FormLabel>Queue</FormLabel>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <NavButton
                      type="button"
                      onClick={() => handleReorder(EngineeringRoadmap__ReorderDirection.Up)}
                      disabled={editingQueueContext.queueIndex === 0}
                      title="Move earlier in queue"
                      style={{ padding: '6px 14px' }}
                    >
                      ↑
                    </NavButton>
                    <NavButton
                      type="button"
                      onClick={() => handleReorder(EngineeringRoadmap__ReorderDirection.Down)}
                      disabled={editingQueueContext.queueIndex >= editingQueueContext.queueLength - 1}
                      title="Move later in queue"
                      style={{ padding: '6px 14px' }}
                    >
                      ↓
                    </NavButton>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                      {editingQueueContext.queueIndex + 1} of {editingQueueContext.queueLength}
                    </span>
                  </div>
                </FormGroup>
              )}

              <FormGroup>
                <FormLabel htmlFor="roadmap-entry-note">Notes</FormLabel>
                <FormTextarea
                  id="roadmap-entry-note"
                  value={formData.note}
                  onChange={(e) => updateField('note', e.target.value)}
                  placeholder="Any additional context..."
                  style={{ minHeight: compactEntryForm ? 48 : 72 }}
                />
              </FormGroup>
            </ScrollModalBody>

            <ScrollModalFooter>
              <ScrollModalActions>
                {editingEntry && (
                  <DangerButton
                    onClick={() => handleDelete('entry' in editingEntry ? editingEntry.entry : editingEntry)}
                    style={{ marginRight: 'auto' }}
                  >
                    Delete
                  </DangerButton>
                )}
                <NavButton onClick={closeEntryModal}>Cancel</NavButton>
                <PrimaryButton onClick={handleEntrySubmit} disabled={submitting}>
                  {submitting ? 'Saving...' : editingEntry ? 'Update' : 'Create'}
                </PrimaryButton>
              </ScrollModalActions>
            </ScrollModalFooter>
          </ScrollModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};
